import type { GameState, Guess, GameStatus } from '../types';
import { STORAGE_KEY } from './constants';

type Persisted = {
  dayIndex: number;
  guesses: Guess[];
  status: GameStatus;
  chillMode: boolean;
  dripStartedAt: number | null;
};

// One-time migration cleanup. Drops legacy keys from earlier versions:
// - seance-state-v1 / seance-stats-v1: the séance-era rebrand keys
// - bsidevideo-state-v1: the 6-row schema; abandoned when the game tightened
//   to 5 rows so an in-flight 6-guess save can't load and trip isOut.
// - bsidevideo-state-v2: pre-drip storage shape. v3 adds chillMode and the
//   per-row drip start timestamp, so old v2 saves are dropped rather than
//   migrated (mid-day reload after upgrade is acceptable collateral).
// Safe to run on every load.
export function migrateLegacyStorage(): void {
  try {
    localStorage.removeItem('seance-state-v1');
    localStorage.removeItem('seance-stats-v1');
    localStorage.removeItem('bsidevideo-state-v1');
    localStorage.removeItem('bsidevideo-state-v2');
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — non-fatal
  }
}

export function save(state: GameState): void {
  const payload: Persisted = {
    dayIndex: state.puzzle.dayIndex,
    guesses: state.guesses,
    status: state.status,
    chillMode: state.chillMode,
    dripStartedAt: state.dripStartedAt,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

// Returns null if no save exists, or the save belongs to a previous day.
export function load(
  currentDayIndex: number,
): Pick<GameState, 'guesses' | 'status' | 'chillMode' | 'dripStartedAt'> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<Persisted>;
    if (data.dayIndex !== currentDayIndex) return null;
    return {
      guesses: data.guesses ?? [],
      status: data.status ?? 'playing',
      chillMode: data.chillMode ?? false,
      dripStartedAt: data.dripStartedAt ?? null,
    };
  } catch {
    return null;
  }
}
